import React, { memo } from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloseRounded,
  AccessTime,
  Remove,
  CheckCircleOutline,
} from "@mui/icons-material";
import { isEmergencyDepartment } from "../../constants/facilityTypes";
import {
  CAPABILITY_VERIFICATION_ELIGIBLE_STATUSES,
  DIAGNOSTIC_CAPABILITY_CATEGORY_ORDER,
  DIAGNOSTIC_CAPABILITY_KEYS,
  DIAGNOSTIC_CAPABILITY_KEYS_BY_CATEGORY,
  getEffectiveDiagnosticCapabilities,
} from "../../constants/diagnosticCapabilities";
import { getCapabilityVerificationStatus } from "./capabilityVerification";
import {
  getCapabilityLabel,
  getMatchTierChipColor,
  getMatchTierLabel,
} from "../../services/facilityMatch";

/**
 * Heading text for the capability panel.
 *
 * Emergency Departments are assumed to have the full diagnostic capability set for matching
 * and LLM purposes (see getEffectiveDiagnosticCapabilities — empty array = full capability
 * for EDs). That assumption is *unverified* until someone claims the facility and a
 * chartmind-supervisor attests to its capabilities. Until then we label the displayed
 * heading "Unconfirmed".
 *
 * INVARIANT: This is display-only. It does NOT change the capabilities used by matching,
 * ranking, or LLM queries — those still treat an unclaimed ED as fully capable.
 * See: SC2_CAREMAP2_CAPABILITY_ATTESTATION_AND_MATCHING.md
 *
 * @param {object | null | undefined} location - Facility/location doc.
 * @returns {string} "Diagnostic capabilities" or "Diagnostic capabilities - Unconfirmed".
 */
function getCapabilityPanelHeading(location) {
  const isUnconfirmedEmergencyDepartment =
    isEmergencyDepartment(location) &&
    !CAPABILITY_VERIFICATION_ELIGIBLE_STATUSES.includes(
      getCapabilityVerificationStatus(location)
    );
  return isUnconfirmedEmergencyDepartment
    ? "Diagnostic capabilities - Unconfirmed"
    : "Diagnostic capabilities";
}

function ModalCapabilityIndicator({ facilityHas, patientNeeds }) {
  if (facilityHas && patientNeeds) {
    return (
      <Box
        component="span"
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: "success.main",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
    );
  }
  if (facilityHas) {
    return (
      <Box
        component="span"
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          border: "2px solid",
          borderColor: "success.main",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
    );
  }
  if (patientNeeds) {
    return <CloseRounded sx={{ fontSize: 16, color: "error.main" }} />;
  }
  return <Remove sx={{ fontSize: 16, color: "text.disabled" }} />;
}

function GenericCapabilityIndicator({ facilityHas }) {
  if (facilityHas) {
    return (
      <CheckCircleOutline
        aria-label="available"
        sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }}
      />
    );
  }
  return (
    <Remove
      aria-label="not available"
      sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }}
    />
  );
}

function GenericCapabilityBreakdown({ location }) {
  const facilityCapSet = new Set(getEffectiveDiagnosticCapabilities(location));

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
        {getCapabilityPanelHeading(location)}
      </Typography>

      {DIAGNOSTIC_CAPABILITY_CATEGORY_ORDER.map((category) => (
        <Box key={category} sx={{ mb: 1.25 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.5, fontSize: "0.65rem", display: "block", mb: 0.5 }}
          >
            {category}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.75,
            }}
          >
            {DIAGNOSTIC_CAPABILITY_KEYS_BY_CATEGORY[category].map((key) => {
              const facilityHas = facilityCapSet.has(key);
              return (
                <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <GenericCapabilityIndicator facilityHas={facilityHas} />
                  <Typography
                    variant="caption"
                    color={facilityHas ? "text.primary" : "text.disabled"}
                  >
                    {getCapabilityLabel(key)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

function TriageCapabilityBreakdown({ matchData, location }) {
  const facilityCaps = getEffectiveDiagnosticCapabilities(location);
  const patientNeeds = Array.isArray(matchData.patientNeeds) ? matchData.patientNeeds : [];
  const patientNeedSet = new Set(patientNeeds);
  const relevantKeys = DIAGNOSTIC_CAPABILITY_KEYS.filter((key) => patientNeedSet.has(key));
  const otherKeys = DIAGNOSTIC_CAPABILITY_KEYS.filter((key) => !patientNeedSet.has(key));
  const tierColors = matchData.matchTier
    ? getMatchTierChipColor(matchData.matchTier)
    : getMatchTierChipColor("none");
  const missingPatientNeeds = (matchData.missingCapabilities || []).filter((key) =>
    patientNeedSet.has(key)
  );

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {getCapabilityPanelHeading(location)}
        </Typography>
        <Chip
          size="small"
          label={getMatchTierLabel(
            matchData.matchTier,
            matchData.matchScore,
            matchData.requiredCapabilitiesCount
          )}
          sx={{
            bgcolor: tierColors.bg,
            color: tierColors.text,
            border: `1px solid ${tierColors.border}`,
          }}
        />
      </Box>

      {isEmergencyDepartment(location) ? (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1.5 }}>
          <AccessTime sx={{ fontSize: 16, color: "text.secondary", mt: 0.25 }} />
          {Number.isFinite(location.score_op18b) && location.score_op18b > 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Typical discharge time: {location.score_op18b} min{" "}
                <Tooltip title="Source: CMS Hospital Compare (measure OP-18b) · updated quarterly by CMS">
                  <Box component="span" sx={{ textDecoration: "underline dotted" }}>
                    · CMS data
                  </Box>
                </Tooltip>
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Median time from arrival to discharge for discharged patients
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Discharge time data not yet available
            </Typography>
          )}
        </Box>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0.75,
        }}
      >
        {relevantKeys.map((key) => (
          <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <ModalCapabilityIndicator
              facilityHas={facilityCaps.includes(key)}
              patientNeeds={patientNeedSet.has(key)}
            />
            <Typography variant="caption">{getCapabilityLabel(key)}</Typography>
          </Box>
        ))}
      </Box>

      {otherKeys.length > 0 ? (
        <>
          <Divider sx={{ my: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Other capabilities at this facility
            </Typography>
          </Divider>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.75,
            }}
          >
            {otherKeys.map((key) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <ModalCapabilityIndicator
                  facilityHas={facilityCaps.includes(key)}
                  patientNeeds={false}
                />
                <Typography variant="caption">{getCapabilityLabel(key)}</Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : null}

      {missingPatientNeeds.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This facility may not offer:{" "}
          {missingPatientNeeds.map(getCapabilityLabel).join(", ")}. Confirm availability before
          your visit.
        </Alert>
      ) : null}
    </Paper>
  );
}

const CapabilityBreakdownPanel = memo(function CapabilityBreakdownPanel({
  matchData,
  location,
}) {
  if (!matchData) {
    return <GenericCapabilityBreakdown location={location} />;
  }

  return <TriageCapabilityBreakdown matchData={matchData} location={location} />;
});

export default CapabilityBreakdownPanel;
